"use client";

import React, { useRef, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReCAPTCHA from "react-google-recaptcha";
import Loader from "./Loader";
import { FaArrowRight } from "react-icons/fa";

const FormCont: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [phone, setPhone] = useState("");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    fullname?: string;
    email?: string;
    phone?: string;
    message?: string;
  }>({});
  const [submitted, setSubmitted] = useState(false);

  /* ---------------------------------------------------
      TYPING EFFECT FOR HEADING
  ----------------------------------------------------*/
  useEffect(() => {
    const words = ["Hello", "Bonjour", "Hola", "Ciao", "Hallo", "Merhaba", "Salam", "Olá"];
    let i = 0;
    let j = 0;
    let isDeleting = false;

    const typingElement = document.querySelector(".typingText") as HTMLElement;

    const typeEffect = () => {
      if (!typingElement) return;

      const currentWord = words[i];

      if (!isDeleting) {
        typingElement.innerHTML = currentWord.substring(0, j + 1);
        j++;

        if (j === currentWord.length) {
          isDeleting = true;
          setTimeout(typeEffect, 1000);
          return;
        }
      } else {
        typingElement.innerHTML = currentWord.substring(0, j - 1);
        j--;

        if (j === 0) {
          isDeleting = false;
          i = (i + 1) % words.length;
        }
      }

      setTimeout(typeEffect, isDeleting ? 80 : 120);
    };

    typeEffect();
  }, []);

  /* ---------------------------------------------------
      RECAPTCHA HANDLER
  ----------------------------------------------------*/
  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  /* ---------------------------------------------------
      FORM VALIDATION
  ----------------------------------------------------*/
  const validateForm = () => {
    const newErrors: any = {};

    if (!fullname) newErrors.fullname = "Full Name is required";
    else if (fullname.length > 60)
      newErrors.fullname = "Fullname must be less than 60 characters.";

    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Please enter a valid email address";

    if (!phone) newErrors.phone = "Phone number is required";
    else if (phone.length < 10 || phone.length > 18)
      newErrors.phone = "Phone number must be 10–18 digits";

    if (!message) newErrors.message = "Message is required";
    else if (message.length > 200)
      newErrors.message = "Message must be less than 200 characters.";

    if (!recaptchaValue) return;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ---------------------------------------------------
      SUBMIT FORM
  ----------------------------------------------------*/
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setLoading(true);

    if (validateForm()) {
      try {
        const response = await fetch("/api/expert", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fullname,
            email,
            phone,
            message,
            recaptcha_token: recaptchaValue,
          }),
        });

        const data = await response.json();

        if (data.success == true) {
          toast.success(data.message);
          setFullname("");
          setEmail("");
          setPhone("");
          setMessage("");
          setRecaptchaValue("");
          setSubmitted(false);
        } else {
          setRecaptchaValue("");
          recaptchaRef.current?.reset();
          toast.error(data.message);
        }
      } catch (error) {
        setRecaptchaValue("");
        recaptchaRef.current?.reset();
        const message =
          error instanceof Error ? error.message : String(error);
        toast.error(message);
      }

      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------
      INPUT CHANGE
  ----------------------------------------------------*/
  const handleInputChange = (field: string, value: string) => {
    if (submitted) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (field === "fullname") setFullname(value);
    if (field === "email") setEmail(value);
    if (field === "phone") setPhone(value);
    if (field === "message") setMessage(value);
  };

  return (
    <>
      {loading && <Loader />}

      <div className=" ">
        
        {/* ====== NEW HEADING WITH TYPING EFFECT ====== */}
        <div className="text-center mb-4">
          <div className="d-flex justify-content-center align-items-center gap-2">
            <span className="fs-1">🙂</span>
            <h1 className="fw-bold display-5">
              Say <span className="typingText"></span>
            </h1>
          </div>

          <p className="text-muted mt-2 display-6 fs-4">
            Have an idea, need advice, or just want to<br/>say hello? We’re all ears.
          </p>
        </div>

        {/* FORM START */}
        <form onSubmit={handleSubmit}>
          
          {/* Full Name + Email + Phone Row */}
          <div className="row">
            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <input
                type="text"
                className="form-control custInput py-2 px-4"
                placeholder="Full Name"
                value={fullname}
                onChange={(e) => handleInputChange("fullname", e.target.value)}
              />
              {errors.fullname && submitted && (
                <div className="text-danger mt-1">{errors.fullname}</div>
              )}
            </div>

            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <input
                type="email"
                className="form-control custInput py-2 px-4"
                placeholder="Email Address"
                value={email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
              {errors.email && submitted && (
                <div className="text-danger mt-1">{errors.email}</div>
              )}
            </div>

            <div className="col-lg-12 col-md-4 col-12 mb-3">
              <PhoneInput
                country={"us"}
                value={phone}
                onChange={(value) => handleInputChange("phone", value)}
                inputClass="form-control w-100"
                containerClass="w-100"
                inputStyle={{
                  height: "66px",
                  borderRadius: "8px",
                  paddingLeft: "80px",
                }}
              />
              {errors.phone && submitted && (
                <div className="text-danger mt-1">{errors.phone}</div>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="mb-3">
            <textarea
              className="form-control custInput"
              placeholder="Enter your message"
              rows={3}
              value={message}
              onChange={(e) =>
                handleInputChange("message", e.target.value)
              }
            ></textarea>
            {errors.message && submitted && (
              <div className="text-danger mt-1">{errors.message}</div>
            )}
          </div>

          {/* Recaptcha */}
          <div className="my-3">
            <ReCAPTCHA
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
              onChange={handleRecaptchaChange}
              ref={recaptchaRef}
            />
          </div>

          {!recaptchaValue && submitted && (
            <div className="text-danger mb-3">
              Please verify that you are not a robot.
            </div>
          )}

          {/* Submit */}
          <div className="d-grid mb-3">
            <Button className="orangeBtn fw-semibold" type="submit" size="lg">
              Submit Now <FaArrowRight />
            </Button>
          </div>

          <ToastContainer />
        </form>
      </div>
    </>
  );
};

export default FormCont;
